import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCurrency } from '../primitives/CDCurrency';
import { CDSectionHeader } from '../primitives/CDSectionHeader';
import {
  CDClosingCostLineItem,
  CDClosingCostSubtotal,
  type ClosingCostLineItemData,
} from './CDClosingCostLineItem';

export interface LoanCostsSection {
  items: ClosingCostLineItemData[];
  subtotal?: number;
}

export interface LoanCostsData {
  originationCharges: LoanCostsSection;
  servicesYouCannotShopFor: LoanCostsSection;
  servicesYouCanShopFor: LoanCostsSection;
  totalLoanCosts: number;
}

export interface CDLoanCostsProps {
  /** Loan costs data */
  data: Partial<LoanCostsData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<LoanCostsData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDLoanCosts - Loan Costs section (A through D) on Page 2
 *
 * Contains:
 * - A. Origination Charges
 * - B. Services You Cannot Shop For
 * - C. Services You Can Shop For
 * - D. TOTAL LOAN COSTS (Borrower-Paid)
 */
export const CDLoanCosts = React.forwardRef<HTMLDivElement, CDLoanCostsProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    const originationItems = data.originationCharges?.items || [];
    const cannotShopItems = data.servicesYouCannotShopFor?.items || [];
    const canShopItems = data.servicesYouCanShopFor?.items || [];

    // Helper to update an item in a section
    const updateSectionItem = (
      section: 'originationCharges' | 'servicesYouCannotShopFor' | 'servicesYouCanShopFor',
      itemIndex: number,
      updatedItem: ClosingCostLineItemData,
    ) => {
      const sectionData = data[section] || { items: [] };
      const updatedItems = sectionData.items.map((item, idx) =>
        idx === itemIndex ? updatedItem : item,
      );
      onChange?.({
        ...data,
        [section]: { ...sectionData, items: updatedItems },
      });
    };

    return (
      <div ref={ref} className={cn('', className)}>
        <CDSectionHeader title="Loan Costs" />

        <table className="w-full border-collapse border-t-0">
          {/* Column Headers */}
          <thead>
            <tr className="cd-text-tiny">
              <th className="border border-t-0 border-cd-black px-1 py-0.5 w-6"></th>
              <th className="border border-t-0 border-cd-black px-1 py-0.5 text-left"></th>
              <th
                className="border border-t-0 border-cd-black px-1 py-0.5 text-center w-16"
                colSpan={2}
              >
                Borrower-Paid
              </th>
              <th
                className="border border-t-0 border-cd-black px-1 py-0.5 text-center w-16"
                colSpan={2}
              >
                Seller-Paid
              </th>
              <th className="border border-t-0 border-cd-black px-1 py-0.5 text-center w-16">
                Paid by
                <br />
                Others
              </th>
            </tr>
            <tr className="cd-text-tiny">
              <th className="border border-cd-black px-1 py-0.5"></th>
              <th className="border border-cd-black px-1 py-0.5"></th>
              <th className="border border-cd-black px-1 py-0.5 text-center w-16">At Closing</th>
              <th className="border border-cd-black px-1 py-0.5 text-center w-16">
                Before Closing
              </th>
              <th className="border border-cd-black px-1 py-0.5 text-center w-16">At Closing</th>
              <th className="border border-cd-black px-1 py-0.5 text-center w-16">
                Before Closing
              </th>
              <th className="border border-cd-black px-1 py-0.5 w-16"></th>
            </tr>
          </thead>

          <tbody>
            {/* A. Origination Charges */}
            <tr>
              <td colSpan={7} className="p-0">
                <div className="cd-subsection-header">
                  <span className="cd-subsection-header-letter">A. Origination Charges</span>
                  <span className="cd-subsection-header-amount">
                    <CDCurrency
                      value={data.originationCharges?.subtotal}
                      className="cd-text-small"
                    />
                  </span>
                </div>
              </td>
            </tr>
            {originationItems.map((item, idx) => (
              <CDClosingCostLineItem
                key={idx}
                data={item}
                editable={editable}
                onChange={(updatedItem) =>
                  updateSectionItem('originationCharges', idx, updatedItem)
                }
              />
            ))}

            {/* B. Services You Cannot Shop For */}
            <tr>
              <td colSpan={7} className="p-0">
                <div className="cd-subsection-header">
                  <span className="cd-subsection-header-letter">
                    B. Services You Cannot Shop For
                  </span>
                  <span className="cd-subsection-header-amount">
                    <CDCurrency
                      value={data.servicesYouCannotShopFor?.subtotal}
                      className="cd-text-small"
                    />
                  </span>
                </div>
              </td>
            </tr>
            {cannotShopItems.map((item, idx) => (
              <CDClosingCostLineItem
                key={idx}
                data={item}
                editable={editable}
                onChange={(updatedItem) =>
                  updateSectionItem('servicesYouCannotShopFor', idx, updatedItem)
                }
              />
            ))}

            {/* C. Services You Can Shop For */}
            <tr>
              <td colSpan={7} className="p-0">
                <div className="cd-subsection-header">
                  <span className="cd-subsection-header-letter">C. Services You Can Shop For</span>
                  <span className="cd-subsection-header-amount">
                    <CDCurrency
                      value={data.servicesYouCanShopFor?.subtotal}
                      className="cd-text-small"
                    />
                  </span>
                </div>
              </td>
            </tr>
            {canShopItems.map((item, idx) => (
              <CDClosingCostLineItem
                key={idx}
                data={item}
                editable={editable}
                onChange={(updatedItem) =>
                  updateSectionItem('servicesYouCanShopFor', idx, updatedItem)
                }
              />
            ))}

            {/* D. Total Loan Costs */}
            <tr>
              <td colSpan={7} className="p-0">
                <div className="cd-section-header flex justify-between items-center">
                  <span>D. TOTAL LOAN COSTS (Borrower-Paid)</span>
                  <span className="cd-text-small">Loan Costs Subtotals (A + B + C)</span>
                </div>
              </td>
            </tr>
            <CDClosingCostSubtotal label="" borrowerTotal={data.totalLoanCosts} />
          </tbody>
        </table>
      </div>
    );
  },
);

CDLoanCosts.displayName = 'CDLoanCosts';
